// Central SEO config for Vibra Marketing
// Single source of truth for per-page titles, descriptions, JSON-LD,
// breadcrumbs and pricing used across the site.

export const SITE = {
  url: "https://vibramarketing.es",
  name: "Vibra Marketing",
  brand: "Vibra Marketing",
  slogan: "La primera agencia de marketing que te escucha",
  locale: "es_ES",
  language: "es",
  ogImage: "https://vibramarketing.es/og-image.jpg",
  whatsapp: "+34614541275",
  whatsappLink: "https://wa.me/34614541275",
};

export const ORGANIZATION_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;

// Sitewide Organization + LocalBusiness + ProfessionalService
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
  "@id": ORGANIZATION_ID,
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/favicon.png`,
  image: SITE.ogImage,
  description:
    "Agencia de marketing digital para PYMEs en España. Planes de suscripción desde 199€/mes IVA incluido, sin permanencia: Meta Ads, TikTok Ads, Google Ads, SEO, SEO Local, Redes Sociales, Email Marketing y Mantenimiento Web.",
  slogan: SITE.slogan,
  telephone: SITE.whatsapp,
  address: {
    "@type": "PostalAddress",
    streetAddress: "Calle San Roque 4",
    postalCode: "28710",
    addressLocality: "El Molar",
    addressRegion: "Madrid",
    addressCountry: "ES",
  },
  areaServed: { "@type": "Country", name: "España" },
  priceRange: "199€ - 5.900€",
  sameAs: [
    "https://www.linkedin.com/company/vibramarketing/",
    "https://www.instagram.com/vibramarketing.es/",
    "https://www.facebook.com/profile.php?id=61554881667961",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Servicios de marketing digital",
    itemListElement: [
      "Meta Ads",
      "TikTok Ads",
      "Google Ads",
      "SEO",
      "SEO Local",
      "Gestión de Redes Sociales",
      "Email Marketing",
      "Mantenimiento Web",
    ].map((name) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE.url,
  name: SITE.name,
  inLanguage: "es-ES",
  publisher: { "@id": ORGANIZATION_ID },
};

// ----------- Per-service SEO data -----------
export type ServiceKind = "mensual" | "puntual";

export interface ServiceSeo {
  slug: string;
  shortName: string;        // for breadcrumb
  metaTitle: string;        // 50-60 chars
  metaDescription: string;  // 140-160 chars
  schemaName: string;       // Service.name
  schemaServiceType: string;
  schemaDescription: string;
  price: number;            // EUR
  kind: ServiceKind;
  keywords: string[];
}

export const SERVICES_SEO: Record<string, ServiceSeo> = {
  // ---------- Mensuales (desde 199€/mes IVA incluido) ----------
  "publicidad-meta": {
    slug: "publicidad-meta",
    shortName: "Meta Ads",
    metaTitle: "Gestión de Meta Ads (Facebook e Instagram) | Vibra Marketing",
    metaDescription:
      "Campañas de Meta Ads gestionadas por expertos: creativos, test A/B y optimización continua. Desde 199€/mes IVA incluido, sin permanencia.",
    schemaName: "Meta Ads — Gestión de Facebook e Instagram Ads",
    schemaServiceType: "Social Media Advertising",
    schemaDescription:
      "Gestión de campañas en Facebook e Instagram: 1 campaña activa, 3 creativos estáticos + 1 vídeo, test A/B y optimización continua. Informe mensual. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["meta ads", "facebook ads", "instagram ads", "agencia meta ads", "publicidad facebook"],
  },
  "publicidad-tiktok": {
    slug: "publicidad-tiktok",
    shortName: "TikTok Ads",
    metaTitle: "Gestión de TikTok Ads para tu negocio | Vibra Marketing",
    metaDescription:
      "Publicidad en TikTok con creativos verticales, test A/B y optimización continua. Desde 199€/mes IVA incluido, sin permanencia.",
    schemaName: "TikTok Ads — Gestión de campañas",
    schemaServiceType: "Social Media Advertising",
    schemaDescription:
      "Gestión de campañas en TikTok Ads: 1 campaña activa, 3 creativos estáticos + 1 vídeo vertical, test A/B y optimización continua. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["tiktok ads", "publicidad tiktok", "agencia tiktok ads", "tiktok for business"],
  },
  "chatgpt-ads": {
    slug: "chatgpt-ads",
    shortName: "ChatGPT Ads",
    metaTitle: "Agencia de ChatGPT Ads: anuncios en ChatGPT | Vibra Marketing",
    metaDescription:
      "Anúnciate en ChatGPT: estudiamos qué preguntan tus clientes, creamos y optimizamos tu campaña con medición real. Desde 199€/mes IVA incluido, sin permanencia.",
    schemaName: "ChatGPT Ads — Gestión de campañas",
    schemaServiceType: "Online Advertising",
    schemaDescription:
      "Gestión de campañas publicitarias en ChatGPT (OpenAI): 1 campaña activa, anuncios conversacionales, medición y optimización continua. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["chatgpt ads", "anuncios en chatgpt", "publicidad chatgpt", "agencia chatgpt ads", "openai ads"],
  },
  "google-ads": {
    slug: "google-ads",
    shortName: "Google Ads",
    metaTitle: "Gestión de Google Ads | Vibra Marketing",
    metaDescription:
      "Campañas de Google Ads: Search, PMax, Display o Shopping con keywords y optimización de pujas. Desde 199€/mes IVA incluido.",
    schemaName: "Google Ads — Gestión de campañas",
    schemaServiceType: "Search Engine Marketing",
    schemaDescription:
      "Gestión de Google Ads: 1 campaña con varios grupos de anuncios (Search, PMax, Display o Shopping), keywords, negativas y optimización de pujas. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["google ads", "sem", "agencia google ads", "performance max", "shopping ads"],
  },
  seo: {
    slug: "seo",
    shortName: "SEO",
    metaTitle: "Servicio SEO: 8 artículos al mes | Vibra Marketing",
    metaDescription:
      "Posicionamiento SEO con 8 artículos mensuales, keyword research y optimización on-page continua. Desde 199€/mes IVA incluido.",
    schemaName: "SEO — Posicionamiento orgánico",
    schemaServiceType: "Search Engine Optimization",
    schemaDescription:
      "Servicio de SEO mensual: 8 artículos de blog al mes (800-1.200 palabras) con imagen personalizada, keyword research, optimización on-page continua y informe mensual. Disponible en planes desde 199€/mes IVA incluido, sin permanencia.",
    price: 199,
    kind: "mensual",
    keywords: ["seo", "posicionamiento web", "agencia seo", "seo españa", "redacción seo"],
  },
  "seo-local": {
    slug: "seo-local",
    shortName: "SEO Local",
    metaTitle: "SEO Local y Google Business | Vibra Marketing",
    metaDescription:
      "Posiciona tu negocio en Google Maps: publicaciones, optimización del perfil e informe con mapa de posiciones. Desde 199€/mes IVA incluido.",
    schemaName: "SEO Local — Google Business Profile",
    schemaServiceType: "Local Search Optimization",
    schemaDescription:
      "SEO Local para negocios físicos: 4 publicaciones mensuales en Google Business, optimización continua del perfil, monitorización e informe con mapa de posiciones locales. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["seo local", "google maps", "google business profile", "google my business", "posicionamiento local"],
  },
  "redes-sociales": {
    slug: "redes-sociales",
    shortName: "Redes Sociales",
    metaTitle: "Gestión de Redes Sociales: 12 piezas/mes | Vibra Marketing",
    metaDescription:
      "12 piezas mensuales (posts, carruseles y vídeos), calendario editorial y publicación en todas tus redes. Desde 199€/mes IVA incluido.",
    schemaName: "Gestión de Redes Sociales",
    schemaServiceType: "Social Media Management",
    schemaDescription:
      "12 piezas de contenido al mes (4 posts, 4 carruseles, 4 vídeos verticales), calendario editorial, publicación en todas tus redes y programación con Metricool. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["gestión redes sociales", "community manager", "instagram", "tiktok", "linkedin"],
  },
  "email-marketing": {
    slug: "email-marketing",
    shortName: "Email Marketing",
    metaTitle: "Email Marketing: correos que venden | Vibra Marketing",
    metaDescription:
      "Hasta 4 correos diseñados al mes con plantillas y automatizaciones en Brevo. Desde 199€/mes IVA incluido, sin permanencia.",
    schemaName: "Email Marketing",
    schemaServiceType: "Email Marketing",
    schemaDescription:
      "Hasta 4 correos diseñados al mes con plantillas, automatizaciones básicas y programación en Brevo. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["email marketing", "brevo", "newsletter", "automatizaciones email"],
  },
  automatizacion: {
    slug: "automatizacion",
    shortName: "Automatización",
    metaTitle: "Automatización con Zapier para tu negocio | Vibra Marketing",
    metaDescription:
      "Conectamos tus herramientas con Zapier: leads al CRM, recordatorios, seguimientos y facturación automáticos. Desde 199€/mes IVA incluido.",
    schemaName: "Automatización de procesos",
    schemaServiceType: "Business Process Automation",
    schemaDescription:
      "Automatizaciones con Zapier: captura de leads al CRM, citas y recordatorios, comunicaciones por email y WhatsApp, y facturación automática. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["automatización", "zapier", "automatizar procesos", "integraciones", "n8n"],
  },
  "mantenimiento-web": {
    slug: "mantenimiento-web",
    shortName: "Mantenimiento Web",
    metaTitle: "Mantenimiento Web mensual | Vibra Marketing",
    metaDescription:
      "Tu web rápida y optimizada: imágenes, velocidad y mejoras continuas cada mes. Desde 199€/mes IVA incluido.",
    schemaName: "Mantenimiento Web",
    schemaServiceType: "Website Maintenance",
    schemaDescription:
      "Optimización técnica mensual de tu web: imágenes, velocidad de carga y recomendaciones de mejora. Webs propias o de terceros. Desde 199€/mes IVA incluido.",
    price: 199,
    kind: "mensual",
    keywords: ["mantenimiento web", "optimización web", "core web vitals", "page speed"],
  },
  crm: {
    slug: "crm",
    shortName: "CRM",
    metaTitle: "CRM y Automatizaciones — Partners Kommo | Vibra",
    metaDescription:
      "Implementación de Kommo CRM por partners oficiales: integraciones con Meta, Google y WhatsApp, y automatizaciones del embudo comercial.",
    schemaName: "CRM y Automatizaciones",
    schemaServiceType: "CRM Implementation",
    schemaDescription:
      "Implementación de Kommo CRM, automatizaciones del embudo comercial e integraciones con Meta, Google y WhatsApp. Mantenimiento mensual incluido en los planes.",
    price: 199,
    kind: "mensual",
    keywords: ["crm", "kommo", "automatizaciones ventas", "whatsapp business api", "pipeline comercial"],
  },
  websites: {
    slug: "websites",
    shortName: "Webs en suscripción",
    metaTitle: "Webs y Landing Pages en suscripción | Vibra Marketing",
    metaDescription:
      "Webs sencillas en suscripción con mantenimiento mensual: hasta 5 páginas, Core Web Vitals y modificaciones incluidas. Desde 199€/mes.",
    schemaName: "Sitios Web y Landing Pages en suscripción",
    schemaServiceType: "Web Design and Maintenance",
    schemaDescription:
      "Diseño y mantenimiento de webs sencillas en suscripción (hasta 5 páginas) más landing pages, monitorización de Core Web Vitals y modificaciones puntuales mensuales.",
    price: 199,
    kind: "mensual",
    keywords: ["diseño web", "landing page", "wordpress", "elementor", "lovable web"],
  },

  // ---------- Puntuales (pago único, financiables en 3 plazos con Klarna) ----------
  consultoria: {
    slug: "consultoria",
    shortName: "Consultoría",
    metaTitle: "Consultoría de Marketing: 320€ | Vibra Marketing",
    metaDescription:
      "Dos sesiones con un experto y un plan de acción documentado en PDF en 7 días. 320€ IVA incluido, financiable con Klarna.",
    schemaName: "Consultoría Estratégica de Marketing",
    schemaServiceType: "Marketing Consultancy",
    schemaDescription:
      "Consultoría estratégica: sesión de diagnóstico de 60 min, análisis completo con PDF entregable en 7 días y sesión de plan de acción de 45 min. 320€ IVA incluido.",
    price: 320,
    kind: "puntual",
    keywords: ["consultoría marketing", "auditoría marketing digital", "plan de marketing"],
  },
  "landing-page": {
    slug: "landing-page",
    shortName: "Landing Page",
    metaTitle: "Landing Page de conversión: 490€ | Vibra Marketing",
    metaDescription:
      "Landing one-page con textos persuasivos y tu VSL editado e integrado. Lista en 10-15 días. 490€ IVA incluido.",
    schemaName: "Landing Page de Conversión",
    schemaServiceType: "Landing Page Design",
    schemaDescription:
      "Landing page de conversión con redacción incluida y edición de VSL o vídeo corto. Entrega en 10-15 días. 490€ IVA incluido.",
    price: 490,
    kind: "puntual",
    keywords: ["landing page", "página de venta", "vsl", "captación leads"],
  },
  branding: {
    slug: "branding",
    shortName: "Branding",
    metaTitle: "Branding y Logo profesional: 500€ | Vibra Marketing",
    metaDescription:
      "Identidad de marca completa: logo, variantes, paleta, tipografías, brandbook y tarjeta de visita. 500€ IVA incluido.",
    schemaName: "Branding e Identidad de Marca",
    schemaServiceType: "Brand Identity Design",
    schemaDescription:
      "Identidad de marca completa: logo con variantes, paleta, tipografías, brandbook y diseño de tarjeta de visita. 3 propuestas y 2 rondas de revisión. 500€ IVA incluido.",
    price: 500,
    kind: "puntual",
    keywords: ["branding", "diseño logo", "identidad corporativa", "brandbook"],
  },
  "crm-implementacion": {
    slug: "crm-implementacion",
    shortName: "Embudo de Ventas",
    metaTitle: "Embudo de Ventas con Kommo CRM: 900€ | Vibra Marketing",
    metaDescription:
      "Embudo de ventas completo con Kommo: setup, pipeline y automatizaciones. 7 meses de licencia + 1 usuario. Integraciones Meta, Google y WhatsApp. 900€ IVA incluido.",
    schemaName: "Embudo de Ventas (Kommo CRM)",
    schemaServiceType: "CRM Implementation",
    schemaDescription:
      "Embudo de ventas completo con Kommo CRM: setup, pipeline, automatizaciones e integraciones con Meta, Google y WhatsApp. Licencia 6+1 meses con 1 usuario incluidos. Renovación con nosotros a 190€/semestre (vs 234€ por su cuenta). 900€ IVA incluido.",
    price: 900,
    kind: "puntual",
    keywords: ["embudo de ventas", "kommo", "partner kommo", "crm pymes", "pipeline comercial"],
  },
  "web-corporativa": {
    slug: "web-corporativa",
    shortName: "Web Corporativa",
    metaTitle: "Diseño Web Corporativa: 1.500€ | Vibra Marketing",
    metaDescription:
      "Web profesional de hasta 8 páginas con textos incluidos, multilingüe y hosting + dominio 1 año. 1.500€ IVA incluido, en 3 plazos con Klarna.",
    schemaName: "Diseño Web Corporativa",
    schemaServiceType: "Corporate Website Design",
    schemaDescription:
      "Web corporativa de hasta 8 páginas con redacción incluida, multilingüe sin límite de idiomas, hosting y dominio el primer año. Entrega en 20-40 días. Pago único de 1.500€ IVA incluido, financiable en 3 plazos con Klarna.",
    price: 1500,
    kind: "puntual",
    keywords: ["diseño web", "web corporativa", "web profesional", "web multilingüe"],
  },
  "tienda-online": {
    slug: "tienda-online",
    shortName: "Tienda Online",
    metaTitle: "Crear Tienda Online completa: 3.000€ | Vibra Marketing",
    metaDescription:
      "Tienda online con productos ilimitados, fichas redactadas y catálogo vinculado a Google y Meta. Hosting y dominio 1 año. 3.000€ IVA incluido.",
    schemaName: "Diseño de Tienda Online",
    schemaServiceType: "E-commerce Development",
    schemaDescription:
      "Tienda online completa con productos ilimitados, redacción de fichas, vinculación con Google Merchant Center y Meta, pasarela de pago y hosting + dominio 1 año. 30-45 días. 3.000€ IVA incluido.",
    price: 3000,
    kind: "puntual",
    keywords: ["tienda online", "ecommerce", "woocommerce", "shopify", "google merchant center"],
  },
  incubadora: {
    slug: "incubadora",
    shortName: "Incubadora",
    metaTitle: "Incubadora de Proyecto: tu negocio digital completo | Vibra",
    metaDescription:
      "Web, embudo, automatizaciones, pagos y medición: todo montado y conectado en 90 días. 5.900€ IVA incluido con 20% de descuento vitalicio en planes.",
    schemaName: "Incubadora de Proyecto Digital",
    schemaServiceType: "Digital Business Setup",
    schemaDescription:
      "Sistema digital completo en 90 días: web, landing, embudo, reservas, automatizaciones comerciales y operativas, pagos, comunicaciones transaccionales y medición. Incluye 20% de descuento vitalicio en planes mensuales. 5.900€ IVA incluido.",
    price: 5900,
    kind: "puntual",
    keywords: ["incubadora negocio digital", "sistema digital", "setup ecommerce", "automatizaciones negocio"],
  },
};

// Helpers ------------------------------------------------
export function serviceUrl(slug: string) {
  return `${SITE.url}/servicios/${slug}`;
}

export function breadcrumbForService(slug: string) {
  const s = SERVICES_SEO[slug];
  if (!s) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE.url}/` },
      { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE.url}/#services` },
      { "@type": "ListItem", position: 3, name: s.shortName, item: serviceUrl(slug) },
    ],
  };
}

export function serviceSchema(slug: string) {
  const s = SERVICES_SEO[slug];
  if (!s) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${serviceUrl(slug)}#service`,
    name: s.schemaName,
    serviceType: s.schemaServiceType,
    description: s.schemaDescription,
    url: serviceUrl(slug),
    provider: { "@id": ORGANIZATION_ID },
    areaServed: { "@type": "Country", name: "España" },
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: String(s.price),
      availability: "https://schema.org/InStock",
      url: serviceUrl(slug),
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(s.price),
        priceCurrency: "EUR",
        unitText:
          s.kind === "mensual"
            ? "mes (desde el plan de 1 servicio, IVA incluido)"
            : "pago único (IVA incluido)",
      },
    },
  };
}

// Pricing ItemList for the homepage
export const PRICING_ITEMLIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Planes de suscripción Vibra Marketing",
  itemListElement: [
    {
      "@type": "Offer",
      position: 1,
      name: "Plan 1 servicio",
      description: "1 servicio mensual a elegir entre 8, gestionado por el equipo de producción de Vibra. Sin permanencia. IVA incluido.",
      price: "199",
      priceCurrency: "EUR",
      url: `${SITE.url}/#pricing`,
    },
    {
      "@type": "Offer",
      position: 2,
      name: "Plan 2 servicios",
      description: "2 servicios mensuales a elegir entre 8, coordinados por una misma persona. Sin permanencia. IVA incluido.",
      price: "490",
      priceCurrency: "EUR",
      url: `${SITE.url}/#pricing`,
    },
    {
      "@type": "Offer",
      position: 3,
      name: "Plan 4 servicios",
      description: "4 servicios mensuales a elegir entre 8, con project manager, reunión semanal y urgencias en 48 horas. Sin permanencia. IVA incluido.",
      price: "1290",
      priceCurrency: "EUR",
      url: `${SITE.url}/#pricing`,
    },
  ],
};

export function breadcrumbForPage(crumbs: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.path.startsWith("http") ? c.path : `${SITE.url}${c.path}`,
    })),
  };
}
